import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'

export type SavedFilter = {
  name: string
  filter: string
  module: string
  system: string
  id: number
}

const SAVED_FILTERS_QUERY_KEY = 'savedFilters'

export function useSavedFilters(systemId: string | undefined) {
  return useQuery<SavedFilter[]>({
    queryKey: [SAVED_FILTERS_QUERY_KEY, systemId],
    queryFn: async () => {
      if (!systemId) {
        throw new Error('SystemId is required to fetch saved filters')
      }

      const response = await apiClient<SavedFilter[]>(`users/me/saved-filters`, { params: { system: systemId } })
      return response
    },
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useSavedFilter(filterId: string) {
  return useQuery({
    queryKey: [`savedFilter`, filterId],
    queryFn: async () => {
      const response = await apiClient<SavedFilter>(`users/me/saved-filters/${filterId}`)
      return response
    },
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useCreateSavedFilter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      name,
      filter,
      module,
      systemId,
    }: {
      name: string
      filter: string
      module: string
      systemId: string | undefined
    }) => {
      if (!systemId) {
        throw new Error('SystemId is required to create a saved filter')
      }

      const response = await apiClient<SavedFilter>(`users/me/saved-filters/create`, {
        method: 'POST',
        data: { name, filter, module, system: systemId },
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SAVED_FILTERS_QUERY_KEY] })
    },
  })
}

export function useDeleteSavedFilter() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (filterId: string) => {
      await apiClient(`users/me/saved-filters/${filterId}/delete`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SAVED_FILTERS_QUERY_KEY] })
    },
  })
}
